import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "@/components/ui/Dropzone";
import { Skeleton } from "@/components/ui/Skeleton";
import { fetchWithAuth } from "@/hooks/useAuth";
import { useCloudflareUpload } from "@/hooks/useCloudflareUpload";
import { Product } from "@/prisma-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image, MoveLeft, MoveRight, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";


function ProductImagePannel({ product }: { product: Product }) {
  const [showUploader, setShowUploader] = useState(false);
  const queryClient = useQueryClient();

  // Single multiple uploader
  const galleryUpload = useCloudflareUpload({
    maxFiles: 20,
    allowedMimeTypes: ["image/*"],
  });

  // Load product images via dedicated endpoint
  type ProductImage = {
    id: number;
    url: string;
    isFeatured?: boolean;
    position?: number;
  };
  const { data: images } = useQuery<ProductImage[], Error>({
    queryKey: ["product-images", product.id],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${product.id}/images`
      );
      if (!res.ok)
        throw new Error("Eroare la încărcarea imaginilor produsului");
      const json = await res.json();
      if (Array.isArray(json)) return json as ProductImage[];
      if (json && Array.isArray(json.data)) return json.data as ProductImage[];
      return [] as ProductImage[];
    },
  });

  // Create images via new CRUD endpoints
  const createProductImages = useMutation({
    mutationFn: async (urls: string[]) => {
      await Promise.all(
        urls.map((url) =>
          fetchWithAuth(`/api/admin/products/${product.id}/images`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url }),
          }).then(async (r) => {
            if (!r.ok) {
              const e = await r.json().catch(() => ({}));
              throw new Error(e.message || "Eroare creare imagine produs");
            }
            return r.json();
          })
        )
      );
    },
    onSuccess: () => {
      toast.success("Imagini încărcate");
      setShowUploader(false);
      galleryUpload.setFiles([]);
      galleryUpload.setErrors([]);
      queryClient.invalidateQueries({
        queryKey: ["product-images", product.id],
      });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare la încărcare"),
  });

  // Pending skeletons count (during upload/create)
  const [pendingSkeletons, setPendingSkeletons] = useState(0);

  // Delete by new endpoint
  const deleteImage = useMutation({
    mutationFn: async (imageId: number) => {
      const res = await fetchWithAuth(
        `/api/admin/products/${product.id}/images/${imageId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la ștergerea imaginii");
      }
    },
    onSuccess: () => {
      toast.success("Imagine ștearsă");
      queryClient.invalidateQueries({
        queryKey: ["product-images", product.id],
      });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  // Set featured (cover)
  const setFeatured = useMutation({
    mutationFn: async (imageId: number) => {
      const res = await fetchWithAuth(
        `/api/admin/products/${product.id}/images/${imageId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isFeatured: true }),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la setarea cover");
      }
    },
    onSuccess: () => {
      toast.success("Setat ca cover");
      queryClient.invalidateQueries({
        queryKey: ["product-images", product.id],
      });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  // Reorder
  const reorderImages = useMutation({
    mutationFn: async (orderIds: number[]) => {
      const res = await fetchWithAuth(
        `/api/admin/products/${product.id}/images/reorder`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order: orderIds }),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la reordonare imagini");
      }
    },
    onSuccess: () => {
      toast.success("Ordine actualizată");
      queryClient.invalidateQueries({
        queryKey: ["product-images", product.id],
      });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  // When uploads finish, create ProductImage records
  useEffect(() => {
    if (galleryUpload.isSuccess && galleryUpload.uploadedResults.length > 0) {
      const urls = galleryUpload.uploadedResults.map((r) => r.publicUrl);
      // Show immediate skeleton placeholders
      setPendingSkeletons(urls.length);
      createProductImages.mutate(urls, {
        onSettled: () => {
          setPendingSkeletons(0);
        },
      });
    }
  }, [galleryUpload.isSuccess, galleryUpload.uploadedResults]);

  const imageList: ProductImage[] = useMemo(() => {
    const arr = images ?? [];
    // Ensure featured first, then by position asc
    return [...arr].sort((a, b) => {
      const fa = a.isFeatured ? 1 : 0;
      const fb = b.isFeatured ? 1 : 0;
      if (fa !== fb) return fb - fa; // featured first
      const pa = a.position ?? 0;
      const pb = b.position ?? 0;
      return pa - pb;
    });
  }, [images]);

  const hasGallery = (images?.length ?? 0) > 0;

  const moveImage = (imageId: number, direction: "left" | "right") => {
    const orderedIds = imageList
      .filter((i) => typeof i.id === "number")
      .map((i) => i.id!) as number[];
    const idx = orderedIds.indexOf(imageId);
    if (idx === -1) return;
    const target = direction === "left" ? idx - 1 : idx + 1;
    if (target < 0 || target >= orderedIds.length) return;
    const next = [...orderedIds];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    reorderImages.mutate(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Imagini Produs
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUploader((s) => !s)}
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Adaugă imagini
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Single uploader + images grid (no separate cover/on-page uploaders) */}

        {/* Uploader */}
        {showUploader && (
          <Dropzone {...galleryUpload}>
            <DropzoneEmptyState />
            <DropzoneContent />
          </Dropzone>
        )}

        {/* Images grid */}
        <div className="space-y-3">
          {hasGallery ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              {imageList.map((img) => (
                <div
                  key={img.id ?? img.url}
                  className="relative group rounded-md overflow-hidden bg-gray-50 border cursor-pointer aspect-square"
                >
                  <img
                    src={img.url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {img.isFeatured && (
                    <div className="absolute left-2 top-2 text-md bg-background text-primary px-3 py-0.5 rounded">
                      Cover
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-start justify-end gap-2 p-2 opacity-0 group-hover:opacity-100 transition">
                    {typeof img.id === "number" && (
                      <>
                        <Button
                          variant="outline"
                          title="Setează cover"
                          onClick={() => setFeatured.mutate(img.id!)}
                          className="text-xs"
                        >
                          Setează cover
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Mută la stânga"
                          onClick={() => moveImage(img.id!, "left")}
                          disabled={reorderImages.isPending}
                        >
                          <MoveLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Mută la dreapta"
                          onClick={() => moveImage(img.id!, "right")}
                          disabled={reorderImages.isPending}
                        >
                          <MoveRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          title="Șterge"
                          onClick={() => deleteImage.mutate(img.id!)}
                          disabled={deleteImage.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {pendingSkeletons > 0 &&
                Array.from({ length: pendingSkeletons }).map((_, idx) => (
                  <Skeleton
                    key={`pending-${idx}`}
                    className="rounded-md bg-gray-200 aspect-square w-full"
                  />
                ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Nu există imagini
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ProductImagePannel;
